/**
 * SIP Interfaces Page
 * 
 * Manage network-level SIP interfaces that can be shared across multiple peers.
 * Uses common components for consistent UI patterns.
 */

import { useState, useMemo } from 'react';
import { 
  Plus, 
  Network, 
  CheckCircle, 
  ShieldCheck,
  Globe,
} from 'lucide-react';
import {
  useSipInterfaces,
  useSipInterfaceUsage,
  useDeleteSipInterface,
  type SipInterface,
} from '../../../hooks/useSipInterfaces';
import { useTlsContexts } from '../../../hooks/useTlsContexts';
import { SipInterfaceEditor } from '../../../components/network/SipInterfaceEditor';
import { handleFormError } from '../../../utils/form-error-handler';

// Common components
import {
  PageHeader,
  StatsGrid,
  StatCard,
  FilterBar,
  ItemCard,
  ItemCardIcon,
  ItemActions,
  EmptyState,
  LoadingState,
  ErrorState,
  DeleteConfirmModal,
  StatusBadge,
  TransportBadge,
  enabledToStatus,
  type FilterOption,
} from '../../../components/common';
import { useViewMode } from '../../../hooks/useViewMode';


// ========================================
// CONSTANTS
// ========================================

type FilterType = 'all' | 'enabled' | 'disabled' | 'UDP' | 'TCP' | 'TLS';

const FILTER_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All Interfaces' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'UDP', label: 'UDP' },
  { value: 'TCP', label: 'TCP' },
  { value: 'TLS', label: 'TLS' },
];

// ========================================
// MAIN COMPONENT
// ========================================

export function SipInterfacesPage() {
  // State management - use user preference for default view mode
  const [viewMode, setViewMode] = useViewMode();
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingInterface, setEditingInterface] = useState<SipInterface | null>(null);
  const [interfaceToDelete, setInterfaceToDelete] = useState<SipInterface | null>(null);

  const { data: interfaces, isLoading, error, refetch, isFetching } = useSipInterfaces();
  const { data: tlsContexts = [] } = useTlsContexts();
  const deleteMutation = useDeleteSipInterface();
  const { data: interfaceUsage, error: interfaceUsageError, isLoading: isInterfaceUsageLoading } = useSipInterfaceUsage(
    interfaceToDelete?.id ?? '',
    !!interfaceToDelete && showDeleteModal,
  );

  // Filter and search logic
  const filteredInterfaces = useMemo(() => {
    if (!interfaces) return [];
    
    return interfaces.filter(iface => {
      // Type/status filter
      let matchesFilter = false;
      if (filterType === 'all') {
        matchesFilter = true;
      } else if (filterType === 'enabled') {
        matchesFilter = iface.enabled === true;
      } else if (filterType === 'disabled') {
        matchesFilter = iface.enabled === false;
      } else {
        matchesFilter = iface.protocol === filterType;
      }

      // Search filter
      const matchesSearch = searchQuery === '' ||
        iface.interfaceId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (iface.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false) ||
        iface.bindAddress.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesFilter && matchesSearch;
    });
  }, [interfaces, filterType, searchQuery]);

  // Sort interfaces by interfaceId
  const sortedInterfaces = [...filteredInterfaces].sort((a, b) => 
    a.interfaceId.localeCompare(b.interfaceId)
  );

  // Calculate statistics
  const stats = useMemo(() => {
    if (!interfaces) return { total: 0, enabled: 0, tls: 0, totalConnections: 0 };
    return {
      total: interfaces.length,
      enabled: interfaces.filter(i => i.enabled).length,
      tls: interfaces.filter(i => i.protocol === 'TLS').length,
      totalConnections: interfaces.reduce((sum, i) => sum + (i.maxConnections || 0), 0),
    };
  }, [interfaces]);

  const tlsContextNameById = useMemo(() => new Map(tlsContexts.map((context) => [context.id, context.name])), [tlsContexts]);

  const handleCreate = () => {
    setEditingInterface(null);
    setShowModal(true);
  };

  const handleEdit = (iface: SipInterface) => {
    setEditingInterface(iface);
    setShowModal(true);
  };

  const handleDeleteClick = (iface: SipInterface) => {
    setInterfaceToDelete(iface);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (interfaceToDelete) {
      try {
        await deleteMutation.mutateAsync(interfaceToDelete.id);
        setShowDeleteModal(false);
        setInterfaceToDelete(null);
      } catch (error) {
        handleFormError(error);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingInterface(null);
  };

  // Loading state
  if (isLoading) {
    return <LoadingState />;
  }

  // Error state
  if (error) {
    return (
      <ErrorState
        title="Failed to load SIP interfaces"
        message={error instanceof Error ? error.message : 'Unknown error'}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="SIP Interfaces"
        description="Manage network-level SIP interfaces that can be shared across multiple peers"
        onRefresh={() => refetch()}
        isRefreshing={isFetching}
        primaryAction={{
          label: 'Create Interface',
          icon: Plus,
          onClick: handleCreate,
        }}
      />

      {/* Statistics Cards */}
      <StatsGrid>
        <StatCard
          label="Total Interfaces"
          value={stats.total}
          icon={Network}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          label="Enabled"
          value={stats.enabled}
          icon={CheckCircle}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
          valueColor="text-green-600"
        />
        <StatCard
          label="TLS Secured"
          value={stats.tls}
          icon={ShieldCheck}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
          valueColor="text-purple-600"
        />
        <StatCard
          label="Max Connections"
          value={stats.totalConnections.toLocaleString()}
          icon={Globe}
          iconBgColor="bg-orange-100"
          iconColor="text-orange-600"
        />
      </StatsGrid>

      {/* Filters and Search */}
      <FilterBar
        searchPlaceholder="Search interfaces..."
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        filterOptions={FILTER_OPTIONS}
        filterValue={filterType}
        onFilterChange={(value) => setFilterType(value as FilterType)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Interfaces Display */}
      {sortedInterfaces.length === 0 ? (
        <EmptyState
          icon={Network}
          title="No SIP interfaces found"
          description="Create your first SIP interface to start routing traffic."
          isFiltered={!!(searchQuery || filterType !== 'all')}
          action={{
            label: 'Create Interface',
            icon: Plus,
            onClick: handleCreate,
          }}
        />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedInterfaces.map((iface) => {
            const isMutualTls = !!(
              iface.tlsConfig?.requireClientCert ||
              iface.tlsConfig?.rejectUnauthorized ||
              iface.tlsConfig?.verifyClient
            );
            const tlsContextName = iface.tlsContextId ? tlsContextNameById.get(iface.tlsContextId) ?? null : null;

            return (
            <ItemCard
              key={iface.id}
              icon={
                <ItemCardIcon
                  icon={<Network className={`w-5 h-5 ${iface.enabled ? 'text-blue-600' : 'text-gray-400'}`} />}
                  bgColor={iface.enabled ? 'bg-blue-100' : 'bg-gray-100'}
                />
              }
              title={iface.interfaceId}
              subtitle={iface.description || `${iface.bindAddress}:${iface.port}`}
              status={<StatusBadge status={enabledToStatus(iface.enabled)} />}
              details={[
                {
                  label: 'Port',
                  value: <span className="font-mono">{iface.port}</span>,
                },
                {
                  label: 'Protocol',
                  value: <TransportBadge transport={iface.protocol as 'UDP' | 'TCP' | 'TLS'} />,
                },
                {
                  label: 'Bind Address',
                  value: <span className="font-mono text-xs">{iface.bindAddress}</span>,
                },
                {
                  label: 'OPTIONS Probe',
                  value: iface.challengeUnmatchedOptions ? 'Challenge' : 'Allow 200 OK',
                },
                {
                  label: 'TLS Context',
                  value: iface.protocol === 'TLS'
                    ? (tlsContextName ?? (iface.tlsContextId ? 'Missing' : 'Unset'))
                    : '—',
                },
              ]}
              tags={
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {iface.tlsConfig && (
                    <span className="flex items-center gap-1 text-green-700">
                      <ShieldCheck className="w-3 h-3" />
                      <span>{isMutualTls ? 'mTLS required' : 'TLS only'}</span>
                      {iface.tlsConfig.teamsCompatible && (
                        <span className="ml-1 px-1 py-0.5 bg-green-100 rounded text-xs">Teams</span>
                      )}
                    </span>
                  )}
                  {iface.challengeUnmatchedOptions && (
                    <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">OPTIONS challenged</span>
                  )}
                </div>
              }
              actions={
                <ItemActions
                  variant="card"
                  actions={[
                    { kind: 'edit', onClick: () => handleEdit(iface) },
                    { kind: 'delete', onClick: () => handleDeleteClick(iface) },
                  ]}
                />
              }
            />
            );
          })}
        </div>
      ) : (
        // List View
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interface</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Port</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Protocol</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bind Address</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TLS Mode</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">OPTIONS Probe</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedInterfaces.map((iface) => {
                const tlsContextName = iface.tlsContextId ? tlsContextNameById.get(iface.tlsContextId) ?? null : null;

                return (
                <tr key={iface.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-gray-900">{iface.interfaceId}</div>
                      {iface.description && (
                        <div className="text-xs text-gray-500">{iface.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-mono text-sm">{iface.port}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div>
                        <TransportBadge transport={iface.protocol as 'UDP' | 'TCP' | 'TLS'} />
                      </div>
                      {iface.protocol === 'TLS' && iface.tlsConfig && (
                        <span className="text-xs text-gray-500">
                          {iface.tlsConfig.requireClientCert ||
                          iface.tlsConfig.rejectUnauthorized ||
                          iface.tlsConfig.verifyClient
                            ? 'mTLS required'
                            : 'TLS only'}
                        </span>
                      )}
                      {iface.protocol === 'TLS' && (
                        <span className="text-xs text-gray-500">
                          TLS context: {tlsContextName ?? (iface.tlsContextId ? 'Missing' : 'Unset')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-mono text-sm text-gray-600">{iface.bindAddress}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {iface.protocol === 'TLS' && iface.tlsConfig ? (
                      <div className="text-sm text-gray-700">
                        <div>
                          {iface.tlsConfig.requireClientCert ||
                          iface.tlsConfig.rejectUnauthorized ||
                          iface.tlsConfig.verifyClient
                            ? 'mTLS required'
                            : 'TLS only'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {iface.tlsContextId ? `TLS context: ${tlsContextNameById.get(iface.tlsContextId) ?? 'Missing'}` : 'TLS context: Unset'}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={iface.challengeUnmatchedOptions ? 'text-sm text-amber-700' : 'text-sm text-gray-500'}>
                      {iface.challengeUnmatchedOptions ? 'Challenge' : 'Allow 200 OK'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={enabledToStatus(iface.enabled)} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <ItemActions
                      variant="table"
                      actions={[
                        { kind: 'edit', onClick: () => handleEdit(iface) },
                        { kind: 'delete', onClick: () => handleDeleteClick(iface) },
                      ]}
                    />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Editor (tabbed modal) */}
      <SipInterfaceEditor
        isOpen={showModal}
        editingInterface={editingInterface}
        onClose={handleCloseModal}
        onSuccess={() => refetch()}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal && !!interfaceToDelete}
        onClose={() => {
          setShowDeleteModal(false);
          setInterfaceToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete SIP Interface"
        entityType="interface"
        entityName={interfaceToDelete?.interfaceId || ''}
        additionalInfo={
          interfaceToDelete && (
            <>
              <p className="text-sm text-gray-600 mb-1 mt-2">Port:</p>
              <p className="text-sm text-gray-700">{interfaceToDelete.port} ({interfaceToDelete.protocol})</p>
              <div className="mt-3 space-y-2 text-sm text-gray-700">
                {isInterfaceUsageLoading ? (
                  <p className="text-gray-500">Loading usage…</p>
                ) : interfaceUsageError ? (
                  <p className="text-red-600">Failed to load usage information.</p>
                ) : interfaceUsage ? (
                  <>
                    <div>
                      <p className="font-medium text-gray-900">Peer usage</p>
                      {interfaceUsage.peers.length > 0 ? (
                        <ul className="mt-1 list-disc pl-5 text-gray-600">
                          {interfaceUsage.peers.map((peer) => (
                            <li key={peer.id}>{peer.name} ({peer.domain})</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-500">No peers use this interface.</p>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Container profile usage</p>
                      {interfaceUsage.containerProfiles.length > 0 ? (
                        <ul className="mt-1 list-disc pl-5 text-gray-600">
                          {interfaceUsage.containerProfiles.map((profile) => (
                            <li key={profile.id}>
                              {profile.name} — {profile.assignedContainerCount} assigned container{profile.assignedContainerCount === 1 ? '' : 's'}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-500">No container profiles use this interface.</p>
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            </>
          )
        }
        isDeleting={deleteMutation.isPending}
        warningMessage="This action is blocked while peers or container profiles still reference the interface."
      />
    </div>
  );
}
